import React, {useState} from "react"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Body} from "@irati/ui/src/Typography"
import WalletBallance from "@irati/wallet/apollo/query/Wallet"
import Form from "@irati/ui/src/Form"
import authentication from "@irati/auth"
import {Checkbox, Hidden, Input} from "@irati/ui/src/FormItem"
import Flex from "@irati/ui/src/Flex"
import {H5} from "@irati/ui/src/Typography/Heading"
import {FormattedCurrency, FormattedMessage, FormattedNumber, injectIntl} from "@irati/utils/lang"
import {Budget, PaymentCurrencyType, WalletEntityType} from "@irati/types"
import {Row} from "react-bootstrap"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import {useBudgetsList, useBudgetsPermissions} from "@irati/wallet/hooks/budget"
import Validation from "@irati/utils/validation"
import BudgetSelect from "./BudgetSelect"
import {distributeRewardPointsSchema, RewardInfo} from "./RewardRecognitionBanner"

interface Props extends InjectIntlProps {
  totRecipients: number
  rewardInfo?: RewardInfo
  onChange: (point: RewardInfo) => void
}
const PointsAssignmentWidget = (props: Props) => {
  const theme = useTheme()
  const {totRecipients, onChange, formatMessage, rewardInfo} = props
  const {data} = useBudgetsList()
  const {canBudget} = useBudgetsPermissions()
  const [selectedBudget, setSelectedBudget] = useState<Budget>()
  const handleUnassigned = () => onChange({...rewardInfo, useUnassigned: !rewardInfo?.useUnassigned})
  const handlePointsPerUser = (newPointsPerUser: string) => {
    const newPoints = parseInt(newPointsPerUser, 10) || 0
    onChange({...rewardInfo, pointsPerUser: newPoints})
  }
  const onBudgetChange = (newBudget: Budget) => {
    setSelectedBudget(newBudget)
    onChange({...rewardInfo, budgetId: newBudget.uuid})
  }
  const totalToPay = rewardInfo?.pointsPerUser ? rewardInfo?.pointsPerUser * 100 * totRecipients : 0
  const unassignedAmount = data?.budgets?.unassigned.amount / 100 || 0
  const isUnassignedBudget = selectedBudget?.uuid === data?.budgets?.unassigned.uuid
  const selectedBudgetAmount = selectedBudget?.amount / 100
  const showUseUnassignedCheckbox = canBudget && unassignedAmount > 0 && selectedBudgetAmount < rewardInfo?.pointsPerUser * totRecipients
  const selectedTotalAmount = isUnassignedBudget ? unassignedAmount : unassignedAmount + selectedBudgetAmount
  return (
    <Flex direction="column" gap={1}>
      <Flex direction="column">
        <Body small weight="bold">
          <FormattedMessage id="recognition.reward.rewardPointsBudget.title" />
        </Body>
        <Row>
          <BudgetSelect onBudgetChange={onBudgetChange} />
        </Row>
      </Flex>
      <Flex gap={theme.spacing.two}>
        <Flex direction="column" flex="1">
          <Body small weight="bold">
            <FormattedMessage id="recognition.reward.admin.distribute.enterPoints.title" />
          </Body>
          <WalletBallance
            variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
            {(apollo) => {
              return (
                <Form>
                  <Input
                    name="pointsPerUser"
                    type="number"
                    min="1"
                    flush
                    value={String(rewardInfo?.pointsPerUser)}
                    placeholder="recognition.reward.admin.distribute.enterPoints.placeholder"
                    onChange={handlePointsPerUser}
                  />
                  <Hidden
                    name="pointsPerUser"
                    value={rewardInfo?.pointsPerUser}
                    key={selectedBudget?.uuid}
                    validation={
                      rewardInfo?.pointsPerUser
                        ? distributeRewardPointsSchema(formatMessage)(
                            apollo.data.wallet.availableBalance,
                            totRecipients,
                            selectedTotalAmount
                          )
                        : null
                    }
                  />
                </Form>
              )
            }}
          </WalletBallance>
        </Flex>
        <Flex direction="column" marginTop={4}>
          <H5>
            <FormattedMessage id="recognition.reward.admin.card.topup.totalToPay" />
          </H5>
          <Flex alignItems="center">
            <WalletBallance
              variables={{
                userId: authentication.user().uuid,
                currency: PaymentCurrencyType.Rp,
                entityType: WalletEntityType.Reward
              }}>
              {(apollo) => (
                <Body weight="bold" color={totalToPay > apollo.data.wallet.amount ? theme.colors.tertiary.r_400 : ""}>
                  <FormattedCurrency value={totalToPay} currency={PaymentCurrencyType.Rp} />
                </Body>
              )}
            </WalletBallance>
          </Flex>
        </Flex>
      </Flex>
      {showUseUnassignedCheckbox && (
        <Row>
          <Checkbox
            name="check_unassigned"
            id="checkUnassigned"
            validation={Validation.bool().oneOf([true], formatMessage({id: "platform.defaultValidation.required"}))}
            checked={rewardInfo?.useUnassigned}
            onChange={handleUnassigned}>
            <Body
              small
              color={rewardInfo?.pointsPerUser * totRecipients - selectedBudgetAmount > unassignedAmount && theme.colors.tertiary.r_400}>
              <FormattedMessage
                id="platform.wallet.reward.budget.distribute.checkbox.insufficientFunds"
                values={{
                  currentBalance: <FormattedNumber value={rewardInfo?.pointsPerUser * totRecipients - selectedBudgetAmount} />,
                  pointAllocation: <FormattedNumber value={unassignedAmount} />
                }}
              />
            </Body>
          </Checkbox>
        </Row>
      )}
    </Flex>
  )
}

export default injectIntl(PointsAssignmentWidget)
